import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BalanceGroupDTO, BillStatus, PaymentMethod } from '@six-pockets/shared';
import { billsApi } from '@/api';
import { CheckIcon } from '@/components/icons';
import { Button, DatePicker, InlineAlert, KeyValue, Modal, MoneyInput, Panel, Section, Spinner, TextField, type SegmentOption } from '@/components/ui';
import { previewPayment } from '@/features/billing/calc';
import { PaymentSection } from '@/features/billing/components/PaymentSection';
import { cx } from '@/utils/cx';
import { formatDayLabel, toDateKey, todayKey } from '@/utils/date';
import { formatDateTime, formatINR, toAmount } from '@/utils/format';
import { billParty } from '../utils';
import styles from '@/styles/components/CollectPaymentModal/style.scss';

/** How long the "Payment saved" tick shows before the modal closes (as on Generate bill). */
const SUCCESS_MS = 1100;

/** Collecting a balance: pay all of it, or part of it now, or waive some or all of it (in place of money). */
const STATUS_OPTIONS: SegmentOption<BillStatus>[] = [
  { value: 'settled', label: 'Settled' },
  { value: 'partial', label: 'Partial' },
  { value: 'waived', label: 'Waived' },
];

interface CollectPaymentModalProps {
  group: BalanceGroupDTO;
  /** Called after the payment is saved (and the success tick has shown). */
  onPaid: () => void;
  onClose: () => void;
}

/**
 * Collects a customer's balance: the total due across their bills, settled in full or partly paid now, by UPI,
 * cash or split, like the bill modal's payment step. The money goes to the oldest bill first; the list shows
 * which bills it clears. A discount (rare) comes off first, oldest bill first and off table time only.
 * Waived (rare; e.g. a customer who is paid for his work by having his bills let off): an amount (the whole balance
 * to start with, editable) and a reason; it clears the oldest bills first and no money is taken. "Record as an
 * expense" (unticked by default) also records it as an expense with that reason. Whatever isn't waived stays due.
 * "Paid today · Change" beside the bills' title (rare, so kept small) records the payment on an earlier day, for a
 * customer who paid then and told the counter later.
 */
export function CollectPaymentModal({ group, onPaid, onClose }: CollectPaymentModalProps) {
  const [status, setStatus] = useState<BillStatus>('settled');
  const [method, setMethod] = useState<PaymentMethod>('upi');
  const [paid, setPaid] = useState('');
  const [upi, setUpi] = useState('');
  const [cash, setCash] = useState('');
  const [discount, setDiscount] = useState('');
  const [waiveAmount, setWaiveAmount] = useState('');
  const [reason, setReason] = useState('');
  const [recordExpense, setRecordExpense] = useState(false);
  // The day the customer paid: today unless staff change it.
  const [paidOn, setPaidOn] = useState(todayKey);
  const [pickingDay, setPickingDay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const waiving = status === 'waived';
  // Waived starts with the whole balance in the amount; staff lower it when only part is let off.
  const changeStatus = useCallback(
    (s: BillStatus) => {
      setStatus(s);
      if (s === 'waived') setWaiveAmount((a) => a || String(group.totalDue));
    },
    [group.totalDue],
  );

  // What each bill can be discounted by: table time not yet discounted, up to what it owes (server allocateDiscount).
  const rooms = useMemo(() => group.bills.map((b) => Math.max(0, Math.min(b.balanceDue, b.bookingsTotal - b.discount))), [group.bills]);
  const maxDiscount = rooms.reduce((a, r) => a + r, 0);
  // No discount with a waiver: the amount waived already says how much is let off.
  const off = waiving ? 0 : toAmount(discount);
  const discountError =
    off > maxDiscount
      ? `At most ${formatINR(maxDiscount)}, the table time due`
      : off > 0 && off >= group.totalDue
        ? 'A discount cannot clear the whole balance'
        : null;
  const toPay = discountError ? group.totalDue : group.totalDue - off;

  const waived = toAmount(waiveAmount);
  const waiveError = !waiving
    ? null
    : !(waived > 0)
      ? 'Enter the amount to waive'
      : waived > group.totalDue
        ? `At most ${formatINR(group.totalDue)}, the total due`
        : !reason.trim()
          ? 'Enter the reason'
          : null;

  const preview = useMemo(
    () => previewPayment({ total: toPay, status, method, upi, cash, paid }),
    [toPay, status, method, upi, cash, paid],
  );
  const backdated = paidOn !== todayKey();

  // What each bill gets, oldest first, as the server applies it: the discount first (off table time), then the money;
  // or, when waiving, the amount waived.
  const shares = useMemo(() => {
    let offLeft = discountError ? 0 : off;
    let left = waiving ? Math.min(waived, group.totalDue) : preview.collect;
    return group.bills.map((b, i) => {
      const d = Math.min(offLeft, rooms[i]!);
      offLeft -= d;
      const take = Math.min(left, b.balanceDue - d);
      left -= take;
      return { discount: d, take };
    });
  }, [group.bills, group.totalDue, rooms, off, discountError, preview.collect, waiving, waived]);

  // A bill can't have been paid before it was made: the newest bill this payment reaches sets the earliest day.
  const newest = group.bills.filter((_, i) => shares[i]!.take + shares[i]!.discount > 0).at(-1);
  const dayError = backdated && newest && toDateKey(new Date(newest.createdAt)) > paidOn ? `Bill #${newest.billNumber} was made after ${formatDayLabel(paidOn)}` : null;
  const blocker = waiving ? (waiveError ?? dayError) : (discountError ?? preview.blocker ?? dayError);

  const submit = async () => {
    if (blocker || submitting || done) return;
    setSubmitting(true);
    setError(null);
    try {
      const billIds = group.bills.map((b) => b.id);
      if (waiving) {
        await billsApi.waive({ billIds, amount: waived, reason: reason.trim(), recordExpense, paidOn: backdated ? paidOn : undefined });
      } else {
        await billsApi.collect({
          billIds,
          amount: preview.collect,
          discount: discountError ? 0 : off,
          payment: { method, upi: method === 'split' ? toAmount(upi) : 0, cash: method === 'split' ? toAmount(cash) : 0 },
          paidOn: backdated ? paidOn : undefined,
        });
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the payment');
    } finally {
      setSubmitting(false);
    }
  };

  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;
  useEffect(() => {
    if (!done) return;
    const timer = setTimeout(() => onPaidRef.current(), SUCCESS_MS);
    return () => clearTimeout(timer);
  }, [done]);

  const n = group.bills.length;
  const close = () => {
    if (!submitting) (done ? onPaid : onClose)();
  };

  const label = waiving
    ? submitting
      ? 'Saving…'
      : done
        ? 'Waived'
        : `Waive ${formatINR(waived)}`
    : submitting
      ? 'Saving…'
      : done
        ? 'Payment saved'
        : `Collect ${formatINR(preview.collect)}`;

  return (
    <Modal
      open
      onClose={close}
      title={group.customer?.name ?? billParty(group.bills[0]!)}
      subtitle={`${n} ${n === 1 ? 'bill' : 'bills'} · ${formatINR(group.totalDue)} due`}
      width={560}
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={close} disabled={submitting || done}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            disabled={!!blocker && !submitting && !done}
            title={blocker ?? undefined}
            aria-busy={submitting}
            icon={submitting ? <Spinner size={16} /> : done ? <CheckIcon size={18} /> : undefined}
            onClick={() => void submit()}
          >
            <span role="status">{label}</span>
          </Button>
        </>
      }
    >
      {error && <InlineAlert message={error} />}

      <Section
        title={n > 1 ? (waiving ? 'Bills · oldest waived first' : 'Bills · oldest paid first') : 'Bill'}
        action={
          pickingDay || backdated ? (
            <div className={styles.paidOn}>
              <DatePicker value={paidOn} onChange={setPaidOn} label={waiving ? 'Day waived' : 'Day paid'} />
            </div>
          ) : (
            <button type="button" className={styles.paidToday} onClick={() => setPickingDay(true)}>
              {waiving ? 'Waived today' : 'Paid today'} · <span>Change</span>
            </button>
          )
        }
      >
        {dayError ? (
          <span className={styles.dayError}>{dayError}</span>
        ) : (
          backdated && <span className={styles.dayNote}>This {waiving ? 'waiver' : 'payment'} will be recorded on {formatDayLabel(paidOn)}, not today.</span>
        )}
        <ul className={styles.bills}>
          {group.bills.map((b, i) => {
            const { discount: d, take } = shares[i]!;
            const reached = take + d;
            const cleared = reached > 0 && reached === b.balanceDue;
            return (
              <li key={b.id} className={styles.bill}>
                <span className={styles.number}>#{b.billNumber}</span>
                <span className={styles.when}>{formatDateTime(b.createdAt)}</span>
                <span className={styles.due}>{formatINR(b.balanceDue)}</span>
                <span className={cx(styles.effect, cleared ? styles.cleared : reached > 0 ? styles.part : styles.none)}>
                  {cleared ? (waiving ? 'Waived' : 'Cleared') : reached > 0 ? `${formatINR(b.balanceDue - reached)} left` : '—'}
                  {d > 0 && <span className={styles.off}>{formatINR(d)} off</span>}
                  {waiving && take > 0 && !cleared && <span className={styles.off}>{formatINR(take)} waived</span>}
                </span>
              </li>
            );
          })}
        </ul>
      </Section>

      <Panel>
        <KeyValue label="Total due" value={formatINR(group.totalDue)} emphasis={off === 0 || !!discountError} />
        {/* Rare (most balances are paid in full). Off table time only, as on the bill; none on condiments-only bills. */}
        {maxDiscount > 0 && !waiving && (
          <div className={styles.discountRow}>
            <label htmlFor="collect-discount" className={styles.discountLabel}>
              Discount
            </label>
            <MoneyInput id="collect-discount" size="md" value={discount} onChange={setDiscount} className={styles.discountInput} max={maxDiscount} />
          </div>
        )}
        {discountError && <span className={styles.discountError}>{discountError}</span>}
        {off > 0 && !discountError && <KeyValue label="To pay" value={formatINR(toPay)} emphasis />}
      </Panel>

      <PaymentSection
        statusOptions={STATUS_OPTIONS}
        status={status}
        onStatusChange={changeStatus}
        method={method}
        onMethodChange={setMethod}
        paid={paid}
        onPaidChange={setPaid}
        upi={upi}
        onUpiChange={setUpi}
        cash={cash}
        onCashChange={setCash}
        balanceDue={preview.balanceDue}
        partialHint={preview.partialHint}
        showPayment={preview.showPayment}
        splitLeft={preview.splitLeft}
      />

      {waiving && (
        <Panel tone="amber">
          <div className={styles.waive}>
            <MoneyInput label="Amount to waive" value={waiveAmount} onChange={setWaiveAmount} max={group.totalDue} />
            <TextField label="Reason" placeholder="e.g. Instagram videos" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={120} />
          </div>
          {waived > 0 && waived < group.totalDue && (
            <div className={styles.waiveDue}>
              <span>Still due after this</span>
              <span className={styles.waiveDueValue}>{formatINR(group.totalDue - waived)}</span>
            </div>
          )}
          <label className={styles.recordExpense}>
            <input type="checkbox" checked={recordExpense} onChange={(e) => setRecordExpense(e.target.checked)} />
            <span>Record as an expense</span>
          </label>
          <span className={cx(styles.waiveNote, waiveError && waiveError !== 'Enter the reason' && styles.waiveError)}>
            {waiveError && waiveError !== 'Enter the reason'
              ? waiveError
              : recordExpense
                ? 'No money is taken. It is added to the Expense ledger under this reason.'
                : 'No money is taken, and no expense is recorded.'}
          </span>
        </Panel>
      )}
    </Modal>
  );
}
